import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const avatar = cva(
  'inline-flex items-center justify-center rounded-full font-semibold text-[var(--on-solid)] uppercase tracking-tight',
  {
    variants: {
      size: {
        sm: 'size-6 text-[10px]',
        md: 'size-8 text-xs',
        lg: 'size-10 text-sm',
        xl: 'size-16 text-lg',
      },
    },
    defaultVariants: { size: 'md' },
  },
);

export interface AvatarProps extends VariantProps<typeof avatar> {
  name: string;
  color?: string;
  className?: string;
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return (first + last).toUpperCase();
}

// Hash determinístico do nome → uma das cores da paleta. Garante que o
// avatar tenha sempre uma cor (a "bolinha") mesmo sem prop color.
const AVATAR_PALETTE = [
  '#d97757', // accent
  '#3d8f5f', // green
  '#6366f1', // indigo
  '#5b8def', // blue
  '#d97706', // amber
  '#a855f7', // purple
  '#0891b2', // cyan
  '#65a30d', // lime
];
function hashColor(name: string): string {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return AVATAR_PALETTE[h % AVATAR_PALETTE.length]!;
}

export function Avatar({ name, color, size, className }: AvatarProps) {
  const bg = color ?? hashColor(name);
  return (
    <span
      className={cn(avatar({ size }), className)}
      style={{ backgroundColor: bg, color: '#fff' }}
      aria-label={name}
    >
      {initials(name)}
    </span>
  );
}
